import { useState } from "react";
import { idempotencyKey, type ApiErrorInfo } from "@/lib/api";
import type { ApiResult } from "@/features/invoicing/api";
import type { CreatePayerRequest, Payer } from "@/features/invoicing/types";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { EmptyState } from "@/components/ui/feedback";
import { COPY } from "./copy";
import { RefusalPanel } from "./refusal-panel";

/**
 * Payer picker for `/stablecoins/invoices/new`.
 *
 * The payer is the invoice's counterparty *record* — a name, a country and an
 * optional tax identity — not an Alloy account, so the picker never reads the
 * organisation's members. It offers the payers the service returned, and can
 * add one through `POST /v1/invoice-payers`.
 *
 * The create form is closed by default: the common path is to pick an existing
 * payer, and a permanently-open second form next to the first is how a page
 * ends up submitting the wrong one.
 *
 * Nothing here decides who may add a payer. `canManagePayers` is the loaded
 * contract's courtesy gate — when it is false the control is disabled with the
 * reason beside it, and the service still refuses the call if it is made.
 */

export interface PayerPickerProps {
  payers: Payer[];
  /** The whole-page first load is still in flight. */
  loading: boolean;
  /** The payer list read failed; the page owns the error surface for it. */
  error: ApiErrorInfo | null;
  selectedId: string;
  onSelect: (payerId: string) => void;
  onCreate: (body: CreatePayerRequest, idempotencyKey: string) => Promise<ApiResult<Payer>>;
  creating: boolean;
  canManagePayers: boolean;
  /** Why the control is disabled, when it is. Null when it is not. */
  scopeNote: string | null;
}

interface PayerDraft {
  displayName: string;
  legalName: string;
  billingEmail: string;
  country: string;
  taxId: string;
}

const EMPTY_DRAFT: PayerDraft = { displayName: "", legalName: "", billingEmail: "", country: "", taxId: "" };

export function PayerPicker({
  payers,
  loading,
  error,
  selectedId,
  onSelect,
  onCreate,
  creating,
  canManagePayers,
  scopeNote,
}: PayerPickerProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<PayerDraft>(EMPTY_DRAFT);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof PayerDraft, string>>>({});
  const [refusal, setRefusal] = useState<ApiErrorInfo | null>(null);

  function set<K extends keyof PayerDraft>(key: K, value: string) {
    setDraft((previous) => ({ ...previous, [key]: value }));
  }

  async function submit() {
    const displayName = draft.displayName.trim();
    const country = draft.country.trim().toUpperCase();
    const errors: Partial<Record<keyof PayerDraft, string>> = {};
    if (displayName === "") errors.displayName = COPY.newPayerNameRequired;
    if (country === "") errors.country = COPY.newPayerCountryRequired;
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setRefusal(null);
    // One key per press: a retry of the *same* press replays the first read,
    // while a second, deliberate add is never de-duplicated into the first.
    const result = await onCreate(
      {
        displayName,
        country,
        ...(draft.legalName.trim() ? { legalName: draft.legalName.trim() } : {}),
        ...(draft.billingEmail.trim() ? { billingEmail: draft.billingEmail.trim() } : {}),
        ...(draft.taxId.trim() ? { taxId: draft.taxId.trim() } : {}),
      },
      idempotencyKey("invoicing_payer_create"),
    );

    if (!result.ok) {
      setRefusal(result.error);
      return;
    }
    onSelect(result.data.payerId);
    setDraft(EMPTY_DRAFT);
    setFieldErrors({});
    setOpen(false);
  }

  const disabled = loading || !canManagePayers;

  return (
    <div className="flex flex-col gap-3">
      {!loading && !error && payers.length === 0 ? (
        <EmptyState
          className="!py-8"
          title={COPY.payerEmpty}
          description={COPY.payerArchivedNote}
        />
      ) : (
        <Field label={COPY.payerLabel} htmlFor="invoice-payer" hint={COPY.payerHint} required>
          <Select
            id="invoice-payer"
            className="w-full"
            value={selectedId}
            disabled={loading || payers.length === 0}
            onChange={(event) => onSelect(event.target.value)}
          >
            <option value="">{loading ? COPY.loading : COPY.payerPlaceholder}</option>
            {payers.map((payer) => (
              <option key={payer.payerId} value={payer.payerId}>
                {payer.displayName}
                {payer.country ? ` — ${payer.country}` : ""}
              </option>
            ))}
          </Select>
        </Field>
      )}

      {!open && (
        <div className="flex flex-col items-start gap-1">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled}
            aria-disabled={disabled}
            onClick={() => {
              setOpen(true);
              setRefusal(null);
              setFieldErrors({});
            }}
          >
            {COPY.newPayerToggle}
          </Button>
          {scopeNote && <p className="text-xs text-muted">{scopeNote}</p>}
        </div>
      )}

      {open && (
        <form
          className="flex flex-col gap-3 rounded-lg border border-line bg-bg p-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className="text-sm font-bold text-ink">{COPY.newPayerHeading}</div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={COPY.newPayerName} htmlFor="payer-name" required error={fieldErrors.displayName}>
              <Input
                id="payer-name"
                value={draft.displayName}
                error={Boolean(fieldErrors.displayName)}
                onChange={(event) => set("displayName", event.target.value)}
              />
            </Field>
            <Field
              label={COPY.newPayerCountry}
              htmlFor="payer-country"
              required
              hint={COPY.newPayerCountryHint}
              error={fieldErrors.country}
            >
              <Input
                id="payer-country"
                value={draft.country}
                maxLength={2}
                autoComplete="country"
                error={Boolean(fieldErrors.country)}
                onChange={(event) => set("country", event.target.value)}
              />
            </Field>
            <Field label={`${COPY.newPayerLegalName} (${COPY.newPayerOptional})`} htmlFor="payer-legal-name">
              <Input
                id="payer-legal-name"
                value={draft.legalName}
                onChange={(event) => set("legalName", event.target.value)}
              />
            </Field>
            <Field label={`${COPY.newPayerEmail} (${COPY.newPayerOptional})`} htmlFor="payer-email">
              <Input
                id="payer-email"
                type="email"
                value={draft.billingEmail}
                onChange={(event) => set("billingEmail", event.target.value)}
              />
            </Field>
            <Field label={`${COPY.newPayerTaxId} (${COPY.newPayerOptional})`} htmlFor="payer-tax-id">
              <Input id="payer-tax-id" value={draft.taxId} onChange={(event) => set("taxId", event.target.value)} />
            </Field>
          </div>

          {refusal && <RefusalPanel error={refusal} className="!mb-0" testId="payer-refusal" />}

          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" loading={creating} disabled={creating}>
              {creating ? COPY.newPayerSubmitting : COPY.newPayerSubmit}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={creating}
              onClick={() => {
                setOpen(false);
                setRefusal(null);
              }}
            >
              {COPY.newPayerCancel}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
